import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/api/client";
import type { Bookmark } from "@/api/types";
import { toast } from "@/lib/toast";
import { SafeLink } from "@/lib/mdLink";
import { Star, X, Trash2, ChevronDown, ChevronRight } from "lucide-react";

export default function BookmarksDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [items, setItems] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .bookmarks()
      .then((r) => setItems(r.items))
      .catch(() => toast("즐겨찾기를 불러오지 못했습니다.", "error"))
      .finally(() => setLoading(false));
  }, [open]);

  const remove = async (id: number) => {
    try {
      await api.bookmarkDelete(id);
      setItems((prev) => prev.filter((b) => b.id !== id));
    } catch {
      toast("삭제에 실패했습니다.", "error");
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex" onClick={onClose}>
      <div className="flex-1 bg-black/40" />
      <aside
        className="flex h-full w-[88vw] max-w-md flex-col border-l border-border bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <span className="flex items-center gap-1.5 font-semibold">
            <Star className="h-4 w-4 text-[#EB4E26]" /> 즐겨찾기
          </span>
          <button onClick={onClose} aria-label="닫기" className="rounded-md p-1 text-muted-foreground hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="p-4 text-sm text-muted-foreground">불러오는 중…</div>
          ) : items.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              저장한 즐겨찾기가 없습니다. 답변 아래 <b>즐겨찾기</b>를 눌러 저장하세요.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {items.map((b) => {
                const isOpen = expanded === b.id;
                const title = (b.question || b.answer).slice(0, 80) || "(제목 없음)";
                return (
                  <li key={b.id} className="rounded-lg border border-border">
                    <div className="flex items-start gap-1 px-2 py-2">
                      <button
                        onClick={() => setExpanded(isOpen ? null : b.id)}
                        className="mt-0.5 shrink-0 text-muted-foreground"
                        aria-label="펼치기"
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                      <button onClick={() => setExpanded(isOpen ? null : b.id)} className="min-w-0 flex-1 text-left">
                        <div className="truncate text-sm font-medium">{title}</div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                          {b.customer ? `${b.customer} · ` : ""}
                          {b.created_at?.slice(0, 16).replace("T", " ")}
                        </div>
                      </button>
                      <button
                        onClick={() => remove(b.id)}
                        className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted"
                        aria-label="삭제"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    {isOpen && (
                      <div className="border-t border-border px-3 py-2">
                        {b.question && (
                          <div className="mb-2 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
                            Q. {b.question}
                          </div>
                        )}
                        <div className="md text-sm">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: SafeLink }}>
                            {b.answer}
                          </ReactMarkdown>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
